import React, { useEffect, useRef, useState, type ReactNode } from 'react'
import {
  Box, Typography, IconButton, Tooltip, Divider,
  List, ListItemButton, ListItemIcon, Paper,
} from '@mui/material'
import ArrowCircleUpIcon from '@mui/icons-material/ArrowCircleUp'
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import EditNoteIcon from '@mui/icons-material/EditNote'
import CloseIcon from '@mui/icons-material/Close'
import { FileTypeIcon } from './FileTypeIcon'
import { FS_MD, ICON_SM, ICON_MD, ICON_LG, ICON_XL } from '../common/theme'
import type { MentionItem } from '../types'

interface ChatInputBarProps {
  // Fired when the user presses Send (or hits Enter in single-line mode).
  // Receives the plain-text contents of the editor and any @-mentions that
  // were inserted. Caller owns the API call.
  onSend: (text: string, mentions: MentionItem[]) => void
  // Optional abort handler. When supplied AND `busy` is true the Send button
  // becomes a Stop button.
  onStop?: () => void
  // Caller-driven in-flight flag - disables the editor and swaps the Send
  // icon for a Stop / processing icon.
  busy?: boolean
  placeholder?: string

  // ── Toolbar configuration ────────────────────────────────────────────
  // The toolbar renders when ANY of `showComposeToggle`, `showCloseButton`,
  // `toolbarStartSlot` or `toolbarEndSlot` is set, OR when `showToolbar` is
  // explicitly true.
  showToolbar?: boolean
  showComposeToggle?: boolean
  showCloseButton?: boolean
  onClose?: () => void
  toolbarStartSlot?: ReactNode      // typically a SearchingScope
  toolbarEndSlot?: ReactNode

  // ── @-mention support ────────────────────────────────────────────────
  // When omitted the editor doesn't watch for `@` (BidQuestionPlanTab's
  // simple workflow chat). Provide it to enable the file/folder mention
  // dropdown - the caller does the search call so the component stays
  // decoupled from any particular API.
  mentionSearch?: (query: string, page: number) => Promise<MentionItem[]>
  mentionPageSize?: number

  testId?: string
}

// Shared chat composer - extracted from KbChatPanel so other surfaces
// (e.g. BidQuestionPlanTab's workflow assistant) can reuse the same
// toolbar + contenteditable input + send/stop button + @-mention picker
// without re-implementing the editor plumbing.
export function ChatInputBar({
  onSend, onStop, busy, placeholder = 'Type a message…',
  showToolbar, showComposeToggle, showCloseButton, onClose,
  toolbarStartSlot, toolbarEndSlot,
  mentionSearch, mentionPageSize = 5,
  testId,
}: ChatInputBarProps) {

  const [editorEmpty, setEditorEmpty] = useState(true)
  const [composeMode, setComposeMode] = useState(false)

  // @-mention state - only meaningful when `mentionSearch` is provided.
  const [mentionQuery, setMentionQuery] = useState<string | null>(null)
  const [mentionIndex, setMentionIndex] = useState(0)
  const [mentionResults, setMentionResults] = useState<MentionItem[]>([])
  const [mentionHasMore, setMentionHasMore] = useState(false)
  const mentionPageRef = useRef(1)

  const editorRef = useRef<HTMLDivElement>(null)
  const mentionListRef = useRef<HTMLUListElement>(null)
  const mentionScrollRef = useRef<HTMLDivElement>(null)
  const prevBusyRef = useRef(false)

  // Re-focus editor when external processing finishes.
  useEffect(() => {
    if (prevBusyRef.current && !busy) {
      setTimeout(() => editorRef.current?.focus(), 0)
    }
    prevBusyRef.current = !!busy
  }, [busy])

  // Run the caller-supplied @-mention search whenever the query changes.
  useEffect(() => {
    if (!mentionSearch) return
    if (mentionQuery === null) { setMentionResults([]); setMentionHasMore(false); return }
    mentionPageRef.current = 0
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const items = await mentionSearch(mentionQuery, 0)
        if (cancelled) return
        setMentionResults(items)
        setMentionHasMore(items.length === mentionPageSize)
        setMentionIndex(0)
      } catch {
        if (!cancelled) { setMentionResults([]); setMentionHasMore(false) }
      }
    }, mentionQuery === '' ? 0 : 150)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [mentionQuery, mentionSearch, mentionPageSize])

  // Scroll the highlighted mention row into view on arrow-up/down.
  useEffect(() => {
    const container = mentionScrollRef.current
    const list = mentionListRef.current
    if (!container || !list) return
    const item = list.children[mentionIndex] as HTMLElement | undefined
    if (!item) return
    const itemTop = item.offsetTop
    const itemBottom = itemTop + item.offsetHeight
    if (itemTop < container.scrollTop) container.scrollTop = itemTop
    else if (itemBottom > container.scrollTop + container.clientHeight) {
      container.scrollTop = itemBottom - container.clientHeight
    }
  }, [mentionIndex])

  // ── Editor helpers ───────────────────────────────────────────────────

  const getTextBeforeCursor = (): string => {
    const sel = window.getSelection()
    if (!sel?.rangeCount) return ''
    const range = sel.getRangeAt(0)
    const editor = editorRef.current
    if (!editor) return ''
    const walker = document.createTreeWalker(editor, NodeFilter.SHOW_TEXT, {
      acceptNode: n => (n.parentElement as HTMLElement)?.dataset.mention ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT,
    })
    let text = ''
    let node: Node | null
    while ((node = walker.nextNode())) {
      if (node === range.endContainer) { text += (node.textContent ?? '').slice(0, range.endOffset); break }
      text += node.textContent ?? ''
    }
    return text.replace(/​/g, '')
  }

  const getEditorContent = (): string => {
    const editor = editorRef.current
    if (!editor) return ''
    const clone = editor.cloneNode(true) as HTMLElement
    clone.querySelectorAll<HTMLElement>('[data-mention]').forEach(span => {
      const type = span.dataset.type ?? 'file'
      span.replaceWith(`@${span.dataset.name}<${type}:${span.dataset.sha}>`)
    })
    clone.querySelectorAll('br').forEach(br => br.replaceWith('\n'))
    return (clone.textContent ?? '').replace(/​/g, '').trim()
  }

  const collectMentions = (): MentionItem[] => {
    const out: MentionItem[] = []
    editorRef.current?.querySelectorAll<HTMLElement>('[data-mention]').forEach(el => {
      const { sha, name, type } = el.dataset
      if (sha && name) out.push({ sha, name, type: (type ?? 'file') as 'file' | 'folder' })
    })
    return out
  }

  const selectMention = (item: MentionItem) => {
    const editor = editorRef.current
    if (!editor) return
    const sel = window.getSelection()
    if (!sel?.rangeCount) { setMentionQuery(null); return }
    const textBefore = getTextBeforeCursor()
    const atIdx = textBefore.lastIndexOf('@')
    if (atIdx < 0) { setMentionQuery(null); return }
    const charsBack = textBefore.length - atIdx
    for (let i = 0; i < charsBack; i++) sel.modify('extend', 'backward', 'character')
    const chip =
      `<span contenteditable="false" data-mention="1" data-sha="${item.sha}" ` +
      `data-name="${item.name}" data-type="${item.type}" ` +
      `style="display:inline-block;background:#f4b632;color:#fff;border-radius:11px;` +
      `padding:1px 8px;font-size:0.75rem;font-weight:600;margin:0 5px 0 2px;` +
      `line-height:1.6;vertical-align:middle;cursor:default;user-select:none">` +
      `@${item.name}</span>​`
    document.execCommand('insertHTML', false, chip)
    const allSpans = editor.querySelectorAll<HTMLElement>('[data-mention]')
    const lastSpan = allSpans[allSpans.length - 1]
    if (lastSpan) {
      let afterNode = lastSpan.nextSibling
      if (!afterNode || afterNode.nodeType !== Node.TEXT_NODE) {
        afterNode = document.createTextNode('​')
        lastSpan.after(afterNode)
      }
      const range = document.createRange()
      range.setStart(afterNode, (afterNode as Text).length)
      range.collapse(true)
      sel.removeAllRanges()
      sel.addRange(range)
    }
    setMentionQuery(null)
    setEditorEmpty(false)
    editor.focus()
  }

  const handleEditorInput = () => {
    const editor = editorRef.current
    if (!editor) return
    setEditorEmpty(!(editor.textContent?.replace(/​/g, '').trim()) && !editor.querySelector('[data-mention]'))
    if (!mentionSearch) return
    const text = getTextBeforeCursor()
    const atIdx = text.lastIndexOf('@')
    if (atIdx >= 0) {
      const query = text.slice(atIdx + 1)
      if (!query.includes('\n')) { setMentionQuery(query); setMentionIndex(0); return }
    }
    setMentionQuery(null)
  }

  const fireSend = () => {
    const text = getEditorContent()
    if (!text || busy) return
    const mentions = collectMentions()
    if (editorRef.current) editorRef.current.innerHTML = ''
    setEditorEmpty(true)
    onSend(text, mentions)
  }

  const handleEditorKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (mentionSearch && mentionQuery !== null && mentionResults.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault()
        if (mentionIndex >= mentionResults.length - 1 && mentionHasMore) {
          const nextPage = mentionPageRef.current + 1
          mentionPageRef.current = nextPage
          mentionSearch(mentionQuery, nextPage).then(items => {
            setMentionResults(items)
            setMentionHasMore(items.length === mentionPageSize)
            setMentionIndex(0)
          }).catch(() => setMentionHasMore(false))
        } else {
          setMentionIndex(i => Math.min(i + 1, mentionResults.length - 1))
        }
        return
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault()
        if (mentionIndex <= 0 && mentionPageRef.current > 0) {
          const prevPage = mentionPageRef.current - 1
          mentionPageRef.current = prevPage
          mentionSearch(mentionQuery, prevPage).then(items => {
            setMentionResults(items)
            setMentionHasMore(true)
            setMentionIndex(items.length - 1)
          }).catch(() => { })
        } else {
          setMentionIndex(i => Math.max(i - 1, 0))
        }
        return
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        e.preventDefault()
        if (mentionResults[mentionIndex]) selectMention(mentionResults[mentionIndex])
        return
      }
      if (e.key === 'Escape') { setMentionQuery(null); return }
    }
    if (e.key === 'Enter' && !e.shiftKey && !composeMode) {
      e.preventDefault()
      fireSend()
    }
  }

  const handleEditorPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    e.preventDefault()
    document.execCommand('insertText', false, e.clipboardData.getData('text/plain'))
    handleEditorInput()
  }

  const showToolbarRow = !!(showToolbar || showComposeToggle || showCloseButton || toolbarStartSlot || toolbarEndSlot)

  return (
    <Box data-testid={testId} sx={{ position: 'relative', flexShrink: 0 }}>

      {/* @-mention dropdown - anchored above the input row */}
      {mentionSearch && mentionQuery !== null && mentionResults.length > 0 && (
        <Paper ref={mentionScrollRef} elevation={4} data-testid="cib_mention_dropdown"
          sx={{ position: 'absolute', bottom: '100%', left: 0, right: 0, mb: 0.5, maxHeight: 220, overflowY: 'auto', zIndex: 20 }}>
          <List dense disablePadding ref={mentionListRef}>
            {mentionResults.map((item, i) => {
              const isSelected = i === mentionIndex
              return (
                <ListItemButton key={item.sha} selected={isSelected}
                  data-testid="cib_mention_item"
                  data-mention-sha={item.sha}
                  data-mention-name={item.name}
                  data-mention-type={item.type}
                  onMouseDown={e => { e.preventDefault(); selectMention(item) }}
                  onMouseEnter={() => setMentionIndex(i)}
                  sx={{ py: 0.5, px: 1.5, gap: 1, '&.Mui-selected': { bgcolor: 'primary.main' }, '&.Mui-selected:hover': { bgcolor: 'primary.dark' } }}>
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    {item.type === 'folder'
                      ? <FolderOutlinedIcon sx={{ fontSize: ICON_SM, color: isSelected ? '#fff' : 'warning.main' }} />
                      : <FileTypeIcon name={item.name} />
                    }
                  </ListItemIcon>
                  <Typography variant="caption" sx={{ fontSize: '0.8rem', color: isSelected ? '#fff' : 'inherit', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.name}</Typography>
                  {item.type === 'file' && item.dirpath && (
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', color: isSelected ? 'rgba(255,255,255,0.75)' : 'text.disabled', flexShrink: 0, ml: 'auto', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '40%' }}>
                      {item.dirpath}
                    </Typography>
                  )}
                </ListItemButton>
              )
            })}
          </List>
        </Paper>
      )}

      {/* Toolbar - rendered only when something asks for it */}
      {showToolbarRow && (
        <Box data-testid="cib_toolbar" sx={{ display: 'flex', alignItems: 'center', px: 1, py: 0.5, gap: 0.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'action.hover', flexShrink: 0 }}>
          {showComposeToggle && (
            <Tooltip title={!composeMode ? 'Compose mode: Shift+Enter for newlines' : 'Single-line mode: Enter to send'}>
              <IconButton size="small" data-testid="cib_compose_btn" onClick={() => setComposeMode(v => !v)}
                sx={{
                  p: 0.5, borderRadius: 1, bgcolor: composeMode ? 'primary.main' : 'transparent', color: composeMode ? '#fff' : 'text.primary',
                  '&:hover': { bgcolor: composeMode ? 'primary.dark' : 'action.hover', color: composeMode ? '#fff' : 'text.primary' }
                }}>
                <EditNoteIcon sx={{ fontSize: ICON_LG }} />
              </IconButton>
            </Tooltip>
          )}
          {showCloseButton && (
            <Tooltip title="Close chat">
              <IconButton size="small" data-testid="cib_close_btn" onClick={onClose} sx={{ p: 0.5 }}>
                <CloseIcon sx={{ fontSize: ICON_MD, opacity: 0.6 }} />
              </IconButton>
            </Tooltip>
          )}
          {(showComposeToggle || showCloseButton) && (toolbarStartSlot || toolbarEndSlot) && (
            <Divider orientation="vertical" flexItem sx={{ mx: 0.25 }} />
          )}
          {toolbarStartSlot}
          {toolbarEndSlot && (
            <Box data-testid="cib_toolbar_right" sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75 }}>
              {toolbarEndSlot}
            </Box>
          )}
        </Box>
      )}

      {/* Input row - contenteditable + send/stop button */}
      <Box sx={{ px: 0.5, py: 1, display: 'flex', alignItems: 'flex-end', gap: 1 }}>
        <Box onClick={() => editorRef.current?.focus()} sx={{
          flex: 1, position: 'relative', border: '1px solid', borderColor: 'divider', borderRadius: 3, cursor: 'text',
          '&:hover': { borderColor: 'primary.light' },
          '&:focus-within': { borderColor: 'primary.main', outline: '1px solid', outlineColor: 'primary.main' },
        }}>
          {editorEmpty && (
            <Typography data-testid="cib_input_placeholder" sx={{ position: 'absolute', top: 0, left: 0, px: 1.5, py: 1, fontSize: FS_MD, color: 'text.disabled', pointerEvents: 'none', userSelect: 'none' }}>
              {placeholder}
            </Typography>
          )}
          <Box component="div" ref={editorRef} contentEditable={!busy} suppressContentEditableWarning
            data-testid="cib_input"
            onInput={handleEditorInput} onKeyDown={handleEditorKeyDown} onPaste={handleEditorPaste}
            sx={{
              px: 1.5, py: 1.0, fontSize: FS_MD,
              minHeight: composeMode ? 200 : 38, maxHeight: composeMode ? 500 : 120, overflowY: 'auto',
              wordBreak: 'break-word', whiteSpace: 'pre-wrap',
              opacity: busy ? 0.5 : 1,
              boxShadow: 'none !important',
            }}
          />
        </Box>
        <Tooltip title={busy ? (onStop ? 'Stop' : 'Streaming…') : 'Send'}>
          <span>
            <IconButton
              data-testid={busy && onStop ? 'cib_stop_btn' : 'cib_send_btn'}
              onClick={busy ? (onStop ?? (() => { })) : fireSend}
              disabled={busy ? !onStop : editorEmpty}
              sx={{
                flexShrink: 0,
                bgcolor: busy ? 'primary.light' : !editorEmpty ? 'primary.main' : undefined,
                color: busy || !editorEmpty ? '#fff' : undefined,
                '&:hover': { bgcolor: 'primary.dark', color: '#fff' },
                '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
                borderRadius: 2,
              }}>
              {busy && onStop
                ? <StopCircleOutlinedIcon sx={{ fontSize: ICON_MD }} />
                : <ArrowCircleUpIcon sx={{ fontSize: ICON_MD }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  )
}

export default ChatInputBar
